import {
  CircularProgress,
  RadioGroup,
} from '@mui/material';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import type {
  IssueMetadata,
  ZodIssueMetadata,
} from '@safc/configuration';
import {
  AdjustedLCAChoice,
  baseline,
  calculateCarbonIntensityScore,
  calculateGreenhouseGasReduction,
  calculateSustainabilityTier,
  calculateSustainabilityTierType,
  calculateTonnesAbated,
  calculateUsabilityTier,
  calculateUsabilityTierInfoForSAFC,
  normalizeIssueMetadata,
  roundCo2Abated,
} from '@safc/configuration';
import { Button } from 'components/ui/buttons/Button/index.js';
import { HelpTooltip } from 'components/ui/common/HelpTooltip/index.js';
import { Radio } from 'components/ui/form/Radio/Radio.js';
import { RadioFormControlLabel } from 'components/ui/form/Radio/Radio.styled.js';
import Markdown from 'markdown-to-jsx';
import { useState } from 'react';

import {
  Footer,
  FooterContent,
} from './IssueForm/components/IssueForm.styled.js';
import {
  Explainer,
  Label,
  ScoreItemWrapper,
  ScoreWrapper,
  Text,
  TierName,
} from './IssueModal.styled.js';

type Props = {
  data: ZodIssueMetadata;
  isSubmitting: boolean;
  onBack: () => void;
  onSubmit: (params: { adjustedLCAChoice: undefined | AdjustedLCAChoice }) => void;
};

export const Results = ({
  data,
  isSubmitting,
  onBack,
  onSubmit,
}: Props) => {
  const metadata = normalizeIssueMetadata(data);
  const sustainabilityTier = calculateSustainabilityTier(metadata as IssueMetadata);
  const usabilityTier = calculateUsabilityTier(metadata, { unitType: 'SAFcA', sustainabilityTier });
  const sustainabilityTierType
    = calculateSustainabilityTierType(sustainabilityTier);
  const usabilityTierInfoForSAFC
    = calculateUsabilityTierInfoForSAFC(usabilityTier);
  const carbonIntensityScore = calculateCarbonIntensityScore(
    metadata,
    { sustainabilityTier },
  );
  const co2Abated = roundCo2Abated(calculateTonnesAbated(
    metadata,
    data.product.quantityMT,
    baseline({ rootMetadata: metadata }),
    carbonIntensityScore,
  ));

  const baselineFuelEmissions = Number(
    data['product'].jetFuelBaselineCarbonIntensity.replace('g', ''),
  );
  const ghgReductions = calculateGreenhouseGasReduction(
    baselineFuelEmissions,
    carbonIntensityScore,
  );

  const isAdjustedLCAValueNegative = carbonIntensityScore < 0;
  const [adjustedLCAChoice, setAdjustedLCAChoice] = useState<AdjustedLCAChoice | undefined>(data.adjustedLCAChoice);

  const adjustedLCAValue = data.adjustedLCAValue ?? carbonIntensityScore;
  const displayNegativeLCAValueChoice
    = isAdjustedLCAValueNegative
      && (sustainabilityTier === 'A' || sustainabilityTier === 'B');

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      mt={2}
      pb={displayNegativeLCAValueChoice ? 12 : 0}
    >
      {/* Form for setting adjusting negative LCA */}
      {displayNegativeLCAValueChoice && (
        <Box display="flex" flexDirection="column" alignItems="center">
          <Label fontWeight={600}>LCA Negative Value Adjustment</Label>
          <Label fontWeight={600}>
            Based on your answers, your SAFc will likely have the following
            attributes
          </Label>

          {' '}
          <br />
          <Typography fontSize={16} fontWeight={400} textAlign="center">
            Your unit meets the requirements to be either SABA advanced or SABA
            eligible except that it has a negative Adjusted LCA value. The
            {' '}
            <a href="https://flysaba.org/saba-saf-sustainability-framework-v3-3-25/" target="_blank" rel="noopener noreferrer">SABA SAF Sustainability Framework</a>
            {' '}
            prohibits negative Adjusted LCA Values for SABA advanced and SABA
            Eligible units. Please choose one the following:
          </Typography>

          {' '}
          <br />
          <Box pl={16}>
            <RadioGroup
              row
              value={adjustedLCAChoice}
              onChange={event => setAdjustedLCAChoice(event.target.value as AdjustedLCAChoice)}
            >
              <RadioFormControlLabel
                value={AdjustedLCAChoice.Adjust}
                control={<Radio />}
                label="I would like to set the adjusted LCA value to 0 and have my unit issued as SABA advanced or SABA eligible"
              />
              <RadioFormControlLabel
                value={AdjustedLCAChoice.Keep}
                control={<Radio />}
                label="I would like to keep the negative adjusted LCA value and have the unit issued as SCS eligible"
              />
            </RadioGroup>
          </Box>
        </Box>
      )}

      {/* Summary */}
      <>
        <ScoreWrapper
          mt={4}
          display="flex"
          flexDirection="column"
          alignItems="center"
        >
          <Box display="flex" flexDirection="column" alignItems="center">
            <TierName data-testid="sustainability-tier">
              Sustainable tier
              {' '}
              {sustainabilityTier}
            </TierName>
            <Typography fontSize={16} fontWeight={400} data-testid="sustainability-tier-type">
              {sustainabilityTierType}
            </Typography>
          </Box>
          <Grid container rowGap={2} width="100%" mt={3.2}>
            <Grid width="100%">
              <ScoreItemWrapper>
                <Label fontWeight={600} data-testid="usability-tier">
                  Usability tier
                  {' '}
                  {usabilityTier}
                </Label>
                <Typography fontSize={12} fontWeight={400} data-testid="usability-tier-info">
                  {usabilityTierInfoForSAFC}
                </Typography>
              </ScoreItemWrapper>
            </Grid>
            <Grid width="100%">
              <ScoreItemWrapper>
                <Box display="flex">
                  <Label fontWeight={600} mr={0.5}>
                    Adjusted LCA
                  </Label>
                  <HelpTooltip title="Adjusted LCA refers to the LCA value obtained following a re-calculation to determine if the SAF batch qualifies for designation as 'SABA-advanced' or 'SABA-eligible' or 'SCS-eligible'​" />
                </Box>
                <Typography fontSize={12} fontWeight={400} data-testid="adjusted-lca">
                  {adjustedLCAValue}
                  {' '}
                  gCO2e/MJ
                </Typography>
              </ScoreItemWrapper>
            </Grid>
            <Grid width="100%">
              <ScoreItemWrapper>
                <Box display="flex">
                  <Label fontWeight={600} mr={0.5}>
                    Abated CO2e
                  </Label>
                </Box>
                <Typography fontSize={12} fontWeight={400} data-testid="co2abated">
                  {co2Abated}
                  {' '}
                  tonnes of CO2e abated
                </Typography>
              </ScoreItemWrapper>
            </Grid>
            <Grid width="100%">
              <ScoreItemWrapper>
                <Label fontWeight={600} mr={0.5}>
                  GHG Reductions
                </Label>
                <Explainer>
                  Greenhouse gas emissions reduction percentage relative to
                  conventional jet fuel.
                </Explainer>
                <Box display="flex">
                  <Typography
                    style={{ marginRight: 4 }}
                    fontSize={12}
                    fontWeight={400}
                    data-testid="ghg-reductions"
                  >
                    {ghgReductions}
                    %
                  </Typography>
                  <HelpTooltip
                    title={(
                      <Markdown>
                        {
                          'A negative value means emissions from producing and distributing the SAF batch is higher than what the emissions would be  from producing and distributing the baseline fossil jet fuel.​ <br><br> The Registry Administrator will reach out to the SAFc issuer if value is negative'
                        }
                      </Markdown>
                    )}
                  />
                </Box>
              </ScoreItemWrapper>
            </Grid>
          </Grid>
        </ScoreWrapper>
        <Box width={509} mt={2}>
          <Text>
            If additional documentation will be required, a registry admin
            will follow-up via email.
          </Text>
          <Text>
            Is something missing, wrong, or unclear?
            {' '}
            <span>Reach out to the help desk before submitting.</span>
          </Text>
        </Box>
      </>

      <Footer>
        <FooterContent>
          <Button
            onClick={onBack}
            color="info"
            variant="contained"
          >
            Back
          </Button>
          <Button
            onClick={() => onSubmit({ adjustedLCAChoice })} // should not be null at the moment of clicking because of `disabled`
            color="primary"
            variant="contained"
            startIcon={isSubmitting && <CircularProgress size={24} />}
            disabled={isSubmitting || (displayNegativeLCAValueChoice && adjustedLCAChoice === undefined)}
          >
            Submit
          </Button>
        </FooterContent>
      </Footer>
    </Box>
  );
};
